import { useCallback, useMemo, useEffect, useState } from 'react';
import { ListEstimates } from './estimates/ListEstimates';
import DocumentViewport from './documents/DocumentViewport';
import { useUnityBuildStore } from '../stores/store';
import { PointEditor } from './svgElements/PointEditor';
import { ColorAndStyleEditor } from './svgElements/ColorAndStyleEditor';
import { useStore } from 'zustand';
import { CloseoutTabs } from './closeout/CloseoutTabs';
import { ToolBar } from './ToolBar';
import { ListChangeOrders } from './estimates/ListChangeOrders';
import { Box, Button, useTheme, styled, alpha } from '@mui/material';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { numenorColors } from '../theme/themeConfig';
import TakeoffPallette from './takeoffPallette/TakeoffPalletteTab';
import { useAssemblyStore } from '../stores/AssemblyStore';
import { useKeyBoardEventStore } from '../stores/keyEventStore';
import TakeoffNotification from './common/TakeoffNotification';
import { useStoreWithEqualityFn } from 'zustand/traditional';

// Import responsive utilities
import { useResponsive, useOrientation, ResponsiveHelpers } from '../theme';
import React from 'react';
import { shallow } from 'zustand/shallow';
import { ListContracts } from './estimates/ListContracts';
import { Z_INDEX_LAYERS, createStackingContext } from '../utils/zIndexLayers';
import { CursorTooltip } from './common/CursorTooltip';
import { AnnotationEditor } from './annotations';
import { useAnnotationStore } from './annotations/annotationStore';

function MasterProjectView() {
  const theme = useTheme();
  const { darkMode } = useCustomTheme();
  const responsive = useResponsive() as ResponsiveHelpers;
  const { isMobile } = responsive;
  const { isLandscape } = useOrientation();

  // State for takeoff palette hover detection
  const [isHoveringTakeoffPalette, setIsHoveringTakeoffPalette] = useState(false);
  const [isHoveringActivationZone, setIsHoveringActivationZone] = useState(false);

  const {
    cursorPosition,
    editingPointX,
    editingPointY,
    selectedProject,
    isOpeningViewport,
    isEditingPoint,
    isViewingEstimateList,
    isViewingColorEditor,
    isViewingExtension,
    isViewingChangeOrderList,
  } = useStoreWithEqualityFn(
    useUnityBuildStore,
    state => ({
      cursorPosition: state.cursorPosition,
      editingPointX: state.editingPointX,
      editingPointY: state.editingPointY,
      currentPage: state.currentPage,
      selectedProject: state.selectedProject,
      isOpeningViewport: state.isOpeningViewport,
      isEditingPoint: state.isEditingPoint,
      isViewingEstimateList: state.isViewingEstimateList,
      isViewingColorEditor: state.isViewingColorEditor,
      isViewingExtension: state.isViewingExtension,
      isViewingChangeOrderList: state.isViewingChangeOrderList,
    }),
    shallow
  );

  const { isViewingTakeoffPallette, isViewingFileManager, takeoffPaletteVisibilityMode } = useStore(
    useKeyBoardEventStore,
    state => ({
      isViewingTakeoffPallette: state.isViewingTakeoffPallette,
      isViewingFileManager: state.isViewingFileManager,
      takeoffPaletteVisibilityMode: state.takeoffPaletteVisibilityMode,
    })
  );

  const { currentTakeoffAssembly, isViewingTakeoffEditor } = useStore(useAssemblyStore, state => ({
    currentTakeoffAssembly: state.currentTakeoffAssembly,
    isViewingTakeoffEditor: state.isViewingTakeoffEditor,
  }));

  // Get annotation editor state
  const {
    isEditingAnnotation,
    editingAnnotationId,
    editingAnnotationData,
    setEditingAnnotation,
    copiedAnnotations,
  } = useAnnotationStore();

  // Keep the editor within viewport bounds
  const keepInView = useCallback((x: number, y: number) => {
    const editorWidth = 250;
    const editorHeight = 590;
    const padding = 20;

    const maxX = window.innerWidth - editorWidth - padding;
    const maxY = window.innerHeight - editorHeight - padding;

    return {
      x: Math.min(Math.max(padding, x), maxX),
      y: Math.min(Math.max(padding, y), maxY),
    };
  }, []);

  // Calculate the position for the PointEditor
  const editorPosition = useMemo(() => {
    const x = editingPointX !== null ? editingPointX + 20 : cursorPosition.x + 20;
    const y = editingPointY !== null ? editingPointY + 20 : cursorPosition.y + 20;
    return keepInView(x, y);
  }, [editingPointX, editingPointY, cursorPosition, keepInView]);

  // Add window resize listener to recalculate position when viewport changes
  useEffect(() => {
    const handleResize = () => {
      // Force re-calculation of annotation editor position on resize
      if (editingAnnotationData) {
        // This will trigger the useMemo to recalculate
        setEditingAnnotation(editingAnnotationId, editingAnnotationData);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [editingAnnotationId, editingAnnotationData, setEditingAnnotation]);

  // Mouse event handlers for takeoff palette hover detection
  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const { clientX } = event;
      const viewportWidth = window.innerWidth;

      // Define activation zone (right side of screen, 200px wide)
      const activationZoneStart = viewportWidth - 200;
      const isInActivationZone = clientX >= activationZoneStart;

      setIsHoveringActivationZone(isInActivationZone);
    };

    const handleMouseLeave = () => {
      setIsHoveringActivationZone(false);
      setIsHoveringTakeoffPalette(false);
    };

    if (takeoffPaletteVisibilityMode === 'hover') {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [takeoffPaletteVisibilityMode]);

  // Determine if takeoff palette should be visible
  const shouldShowTakeoffPalette = useMemo(() => {
    if (!isViewingTakeoffPallette || isViewingFileManager) {
      return false;
    }

    switch (takeoffPaletteVisibilityMode) {
      case 'always':
        return true;
      case 'hover':
        return isHoveringActivationZone || isHoveringTakeoffPalette;
      case 'hidden':
        return false;
      default:
        return true;
    }
  }, [
    isViewingTakeoffPallette,
    isViewingFileManager,
    takeoffPaletteVisibilityMode,
    isHoveringActivationZone,
    isHoveringTakeoffPalette,
  ]);

  // Background with blueprint image
  const backgroundStyle = darkMode
    ? {
        background: `
          linear-gradient(135deg, ${alpha('#111827', 0.85)} 0%, ${alpha('#1f2937', 0.85)} 50%, ${alpha('#111827', 0.85)} 100%),
          url('/desktop_blueprint.webp')
        `,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: { xs: 'scroll', md: 'fixed' },
      }
    : {
        background: `
          linear-gradient(135deg, ${alpha(numenorColors[50], 0.78)} 0%, ${alpha(numenorColors[100], 0.78)} 50%, ${alpha(numenorColors[50], 0.78)} 100%),
          url('/desktop_blueprint.webp')
        `,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: { xs: 'scroll', md: 'fixed' },
      };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        ...backgroundStyle,
        color: theme.palette.text.primary,
        position: 'relative',
        overflow: 'auto',
      }}
      role="main"
      aria-label="Project view"
    >
      <TakeoffNotification />

      {isViewingEstimateList && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            p: { xs: 1, sm: 2 },
            gap: { xs: 1, sm: 2 },
          }}
          role="region"
          aria-label="Estimates and contracts section"
        >
          <Box sx={{ width: isMobile ? '100%' : '50%' }}>
            <ListEstimates />
          </Box>
          <Box sx={{ width: isMobile ? '100%' : '50%' }}>
            <ListContracts />
          </Box>
        </Box>
      )}

      {selectedProject.id && isOpeningViewport && (
        <>
          {/* ToolBar - fixed below MainMenu */}
          <Box
            sx={{
              position: 'fixed',
              top: { xs: '64px', sm: '58px' }, // Below MainMenu
              left: 0,
              right: 0,
              zIndex: Z_INDEX_LAYERS.UI_COMPONENTS.TOOLBAR_CONTAINER,
              backgroundColor: theme.palette.background.paper,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              ...createStackingContext.full,
            }}
          >
            <ToolBar />
          </Box>

          {/* DocumentViewport - fills remaining space */}
          <Box
            sx={{
              position: 'fixed',
              top: { xs: 'calc(64px + 56px)', sm: 'calc(58px + 56px)' }, // Below MainMenu + ToolBar
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: theme.palette.background.default,
            }}
            role="region"
            aria-label="Document viewport"
          >
            <DocumentViewport />
          </Box>

          {/* TakeoffPallette - positioned over viewport */}
          {shouldShowTakeoffPalette && (
            <Box
              className="TakeoffPalletteContainer"
              role="complementary"
              aria-label="Takeoff palette"
              onMouseEnter={() => setIsHoveringTakeoffPalette(true)}
              onMouseLeave={() => setIsHoveringTakeoffPalette(false)}
              sx={{
                position: 'fixed',
                top: { xs: 'calc(64px + 64px)', sm: 'calc(58px + 64px)' }, // Below MainMenu + ToolBar
                right: { xs: 8, sm: 16 },
                width: isMobile ? (isLandscape ? '40%' : '90%') : 500,
                maxHeight: { xs: 'calc(100vh - 140px)', sm: 'calc(100vh - 130px)' },
                borderRadius: 1,
                overflow: 'hidden',
                boxShadow: theme.shadows[4],
                zIndex: Z_INDEX_LAYERS.SYSTEM.TAKEOFF_PALETTE,
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                // Add transition for smooth show/hide
                transition: 'opacity 0.2s ease-in-out, transform 0.2s ease-in-out',
                opacity: shouldShowTakeoffPalette ? 1 : 0,
                transform: shouldShowTakeoffPalette ? 'translateX(0)' : 'translateX(100%)',
              }}
            >
              <TakeoffPallette />
            </Box>
          )}

          {/* Color and style editor - positioned over viewport */}
          {isViewingColorEditor && (
            <Box
              className="ColorAndStyleEditorContainer"
              role="complementary"
              aria-label="Color and style editor"
              sx={{
                position: 'fixed',
                top: { xs: 'calc(64px + 64px)', sm: 'calc(58px + 64px)' }, // Below MainMenu + ToolBar
                right: isViewingTakeoffPallette ? 16 + 500 + 16 : { xs: 8, sm: 16 },
                width: isMobile ? (isLandscape ? '40%' : '90%') : 500,
                maxHeight: { xs: 'calc(100vh - 140px)', sm: 'calc(100vh - 130px)' },
                borderRadius: 1,
                overflow: 'hidden',
                boxShadow: theme.shadows[4],
                zIndex: 950,
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              }}
            >
              <ColorAndStyleEditor />
            </Box>
          )}
        </>
      )}

      {isEditingPoint && currentTakeoffAssembly && isViewingTakeoffEditor && (
        <Box
          role="dialog"
          aria-label="Point editor container"
          sx={{
            position: 'fixed',
            top: { xs: 'calc(64px + 64px)', sm: 'calc(58px + 64px)' }, // Below MainMenu + ToolBar
            right: isViewingTakeoffPallette ? 16 + 500 + 16 : { xs: 8, sm: 16 },
            width: isMobile ? (isLandscape ? '40%' : '90%') : 500,
            maxHeight: { xs: 'calc(100vh - 140px)', sm: 'calc(100vh - 130px)' },
            borderRadius: 1,
            overflow: 'hidden',
            boxShadow: theme.shadows[4],
            zIndex: 950,
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          }}
        >
          <div
            style={{
              position: 'fixed',
              top: editorPosition.y,
              left: editorPosition.x,
              pointerEvents: 'auto',
            }}
          >
            <PointEditor disableAutoPositioning={true} initialPosition={{ x: 0, y: 0 }} />
          </div>
        </Box>
      )}

      {/* Annotation editor - positioned like ColorAndStyleEditor */}
      {isEditingAnnotation && editingAnnotationData && (
        <Box
          className="AnnotationEditorContainer"
          role="complementary"
          aria-label="Annotation editor"
          sx={{
            position: 'fixed',
            top: { xs: 'calc(64px + 64px)', sm: 'calc(58px + 64px)' }, // Below MainMenu + ToolBar
            right: (() => {
              const base = { xs: 8, sm: 16 } as any;
              // Stack to the right of ColorAndStyleEditor and TakeoffPalette if visible
              if (isViewingTakeoffPallette && isViewingColorEditor) return 16 + 500 + 16 + 500 + 16;
              if (isViewingColorEditor) return 16 + 500 + 16;
              if (isViewingTakeoffPallette) return 16 + 500 + 16;
              return base;
            })(),
            width: isMobile ? (isLandscape ? '40%' : '90%') : 350,
            maxHeight: { xs: 'calc(100vh - 140px)', sm: 'calc(100vh - 130px)' },
            borderRadius: 1,
            overflow: 'hidden',
            boxShadow: theme.shadows[4],
            zIndex: 951,
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <AnnotationEditor
            annotation={editingAnnotationData}
            position={{ x: 0, y: 0 }}
            onClose={() => setEditingAnnotation(null, null)}
            onCopy={() => {
              setEditingAnnotation(null, null);
            }}
            onCut={() => {
              setEditingAnnotation(null, null);
            }}
            onPaste={() => {
              setEditingAnnotation(null, null);
            }}
            onDelete={() => {
              setEditingAnnotation(null, null);
            }}
            hasCopiedItems={copiedAnnotations.length > 0}
            isCutting={false}
          />
        </Box>
      )}

      {isViewingExtension && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100vh',
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(66, 66, 66, 0.95)'
                : 'rgba(128, 128, 128, 0.95)',
            zIndex: 1300,
            display: 'flex',
            overflow: 'hidden',
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Extension view"
        >
          <Box
            sx={{
              padding: { xs: '10px', sm: '20px' },
              width: '100%',
              height: '100%',
              display: 'flex',
            }}
          >
            <CloseoutTabs />
          </Box>
        </Box>
      )}

      {isViewingChangeOrderList && !isOpeningViewport && (
        <Box role="region" aria-label="Change orders section">
          <ListChangeOrders />
        </Box>
      )}

      {/* Cursor tooltip for annotation modes */}
      <CursorTooltip />
    </Box>
  );
}

export const StyledButton = styled(Button)(({ theme }) => ({
  width: 50,
  height: 50,
  borderRadius: '50%',
  padding: 5,
  minWidth: 'unset',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
  color: theme.palette.text.primary,
  boxShadow: theme.shadows[2],
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.action.hover : '#e0e0e0',
    transform: 'scale(1.1)',
    boxShadow: theme.shadows[4],
  },
}));

export default React.memo(MasterProjectView);
